import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, SendIcon, DownloadIcon, CheckCircleIcon, AlertTriangleIcon, CalendarIcon, FileTextIcon, TrendingUpIcon, ClockIcon } from 'lucide-react';
import { NIGERIAN_AIRLINES, MOCK_INVOICES } from '../../constants/data';
import { formatNaira } from '../../utils/formatCurrency';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Table } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { MetricGrid } from '../../components/features/MetricCard';
export function InvoiceManagement() {
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const metrics = [{
    label: 'Total Outstanding',
    value: formatNaira(8750000),
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-744" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Paid This Month',
    value: formatNaira(12500000),
    change: {
      value: 18.5,
      trend: 'up' as const
    },
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-745" />,
    iconColor: 'bg-success-100 text-success-600'
  }, {
    label: 'Overdue Invoices',
    value: '2',
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-746" />,
    iconColor: 'bg-error-100 text-error-600'
  }, {
    label: 'Pending Invoices',
    value: '3',
    icon: <ClockIcon className="w-5 h-5" data-id="element-747" />,
    iconColor: 'bg-primary-100 text-primary-600'
  }];
  const columns = [{
    key: 'invoiceNumber',
    header: 'Invoice #',
    sortable: true
  }, {
    key: 'airline',
    header: 'Airline',
    render: (value: unknown) => <div className="flex items-center gap-2" data-id="element-748">
          <div className="w-8 h-8 bg-surface-100 rounded-lg flex items-center justify-center" data-id="element-749">
            <FileTextIcon className="w-4 h-4 text-surface-500" data-id="element-750" />
          </div>
          <span className="font-medium" data-id="element-751">
            {(value as {
          name: string;
        }).name}
          </span>
        </div>
  }, {
    key: 'period',
    header: 'Period',
    render: (value: unknown) => {
      const period = value as {
        start: string;
        end: string;
      };
      return <span className="text-body-sm text-surface-600" data-id="element-752">
            {formatDateRange(period.start, period.end)}
          </span>;
    }
  }, {
    key: 'refundCount',
    header: 'Refunds',
    render: (value: unknown) => <span className="px-2 py-1 bg-surface-100 text-surface-700 text-caption font-medium rounded-md" data-id="element-753">
          {value as number} refunds
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    sortable: true,
    render: (value: unknown) => <span className="tabular-nums font-semibold text-surface-900" data-id="element-754">
          {formatNaira(value as number)}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    render: (value: unknown) => {
      const status = value as string;
      const badgeStatus = status === 'paid' ? 'complete' : status === 'overdue' ? 'failed' : status === 'sent' ? 'processing' : 'pending';
      return <StatusBadge status={badgeStatus} size="sm" data-id="element-755">
            {status}
          </StatusBadge>;
    }
  }, {
    key: 'dueDate',
    header: 'Due Date',
    sortable: true,
    render: (value: unknown, row: (typeof MOCK_INVOICES)[0]) => {
      const isOverdue = row.status === 'overdue';
      return <span className={`text-body-sm ${isOverdue ? 'text-error-600 font-medium' : 'text-surface-600'}`} data-id="element-756">
            {formatDate(value as string)}
            {isOverdue && <span className="ml-2 text-caption text-error-500" data-id="element-757">Overdue</span>}
          </span>;
    }
  }, {
    key: 'actions',
    header: '',
    render: (_: unknown, row: (typeof MOCK_INVOICES)[0]) => <div className="flex items-center gap-1" data-id="element-758">
          <motion.button className="p-2 text-surface-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-759">
            <DownloadIcon className="w-4 h-4" data-id="element-760" />
          </motion.button>
          {row.status === 'draft' && <motion.button className="p-2 text-surface-400 hover:text-success-500 hover:bg-success-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-761">
              <SendIcon className="w-4 h-4" data-id="element-762" />
            </motion.button>}
          {row.status === 'sent' && <motion.button className="p-2 text-surface-400 hover:text-success-500 hover:bg-success-50 rounded-lg transition-colors" whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-763">
              <CheckCircleIcon className="w-4 h-4" data-id="element-764" />
            </motion.button>}
        </div>
  }];
  const handleGenerate = async () => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsGenerating(false);
    setShowGenerateModal(false);
  };
  return <div className="space-y-6" data-id="element-765">
      {/* Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-766">
        <MetricGrid metrics={metrics} columns={4} data-id="element-767" />
      </motion.div>

      {/* Actions */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="flex justify-end" data-id="element-768">
        <motion.div whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-769">
          <Button icon={<PlusIcon className="w-4 h-4" data-id="element-771" />} onClick={() => setShowGenerateModal(true)} className="shadow-lg shadow-primary-500/20" data-id="element-770">
            Generate Invoice
          </Button>
        </motion.div>
      </motion.div>

      {/* Invoice Table */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-772">
        <Card variant="elevated" padding="none" className="shadow-lg border-0 overflow-hidden" data-id="element-773">
          <div className="p-6 border-b border-surface-100 flex items-center justify-between" data-id="element-774">
            <div data-id="element-775">
              <h2 className="text-heading-lg font-bold text-surface-900" data-id="element-776">
                All Invoices
              </h2>
              <p className="text-body-sm text-surface-500 mt-1" data-id="element-777">
                Manage airline invoices and payments
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-778">
              <span className="text-body-sm text-surface-500" data-id="element-779">
                {MOCK_INVOICES.length} invoices
              </span>
            </div>
          </div>
          <Table columns={columns} data={MOCK_INVOICES} keyExtractor={row => row.id} data-id="element-780" />
        </Card>
      </motion.div>

      {/* Generate Invoice Modal */}
      <Modal isOpen={showGenerateModal} onClose={() => setShowGenerateModal(false)} title="Generate Invoice" size="md" data-id="element-781">
        <div className="space-y-4" data-id="element-782">
          <Select label="Airline" placeholder="Select airline" options={NIGERIAN_AIRLINES.map(airline => ({
          value: airline.id,
          label: airline.name
        }))} value={selectedAirline} onChange={setSelectedAirline} data-id="element-783" />
          <div className="grid grid-cols-2 gap-4" data-id="element-784">
            <Input label="From Date" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-786" />} data-id="element-785" />
            <Input label="To Date" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-788" />} data-id="element-787" />
          </div>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="p-4 bg-primary-50 rounded-xl border border-primary-100" data-id="element-789">
            <div className="flex items-start gap-3" data-id="element-790">
              <FileTextIcon className="w-5 h-5 text-primary-600 mt-0.5" data-id="element-791" />
              <div data-id="element-792">
                <p className="text-body-sm font-medium text-primary-700" data-id="element-793">
                  Invoice Preview
                </p>
                <p className="text-body-sm text-primary-600" data-id="element-794">
                  This will generate an invoice for all completed refunds within
                  the selected period.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <ModalFooter data-id="element-795">
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)} data-id="element-796">
            Cancel
          </Button>
          <Button disabled={!selectedAirline || !dateFrom || !dateTo} onClick={handleGenerate} loading={isGenerating} icon={<FileTextIcon className="w-4 h-4" data-id="element-798" />} data-id="element-797">
            Generate Invoice
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}